import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/Modal';
import Tooltip from './Tooltip.js';
import { getConfigValue } from '../util/getConfigValue.js';

function OpportunityScoreModal({
	show,
	onHide,
	selectedOpportunityRow,
	appState,
	getOpportunityScoreClassName,
	getOpportunityScoreDisplay,
	formatOpportunityMetricValue,
}) {
	const oppScoreModalTitle = getConfigValue(appState.config?.Target?.SelectHcp?.OppScore?.Modal?.Title, appState)?.Value || 'Strategic Value For Sales Reps';
	const oppScoreProviderLabel = getConfigValue(appState.config?.Target?.SelectHcp?.OppScore?.Provider?.Label, appState)?.Value || 'Provider';
	const oppScoreMetricLabel = getConfigValue(appState.config?.Target?.SelectHcp?.OppScore?.OpportunityScore?.Label, appState)?.Value || 'Opportunity Score';
	const oppTrxLabel = getConfigValue(appState.config?.Target?.SelectHcp?.OppScore?.OppTrx?.Label, appState)?.Value || 'Opportunity TRx';
	const brandTrxLabel = getConfigValue(appState.config?.Target?.SelectHcp?.OppScore?.BrandTrx?.Label, appState)?.Value || 'Brand TRx (Within 6 Months)';
	const marketTrxLabel = getConfigValue(appState.config?.Target?.SelectHcp?.OppScore?.MarketTrx?.Label, appState)?.Value || 'Market TRx (Within 6 Months)';
	const opportunityMarketTrxLabel = getConfigValue(appState.config?.Target?.SelectHcp?.OppScore?.OpportunityMarketTrx?.Label, appState)?.Value || 'Opportunity Market TRx';
	const isMarketTrxEnabled = String(getConfigValue(appState.config?.Target?.SelectHcp?.OppScore?.MarketTrx?.Enabled, appState)?.Value).toLowerCase() === 'true';
	const oppScoreProviderTooltipText = getConfigValue(appState.config?.Target?.SelectHcp?.OppScore?.Modal?.Provider?.ToolTip?.Label, appState)?.Value || '';
	const oppScoreMetricTooltipText = getConfigValue(appState.config?.Target?.SelectHcp?.OppScore?.Modal?.OppScore?.ToolTip?.Label, appState)?.Value || '';
	const oppTrxTooltipText = getConfigValue(appState.config?.Target?.SelectHcp?.OppScore?.Modal?.OppTrx?.ToolTip?.Label, appState)?.Value || '';
	const brandTrxTooltipText = getConfigValue(appState.config?.Target?.SelectHcp?.OppScore?.Modal?.BrandTrx?.Tooltip?.Label, appState)?.Value || '';
	const marketTrxTooltipText = getConfigValue(appState.config?.Target?.SelectHcp?.OppScore?.Modal?.MarketTrx?.Tooltip?.Label, appState)?.Value || '';
	const opportunityMarketTrxTooltipText = getConfigValue(appState.config?.Target?.SelectHcp?.OppScore?.Modal?.OpportunityMarketTrx?.Tooltip?.Label, appState)?.Value || '';
	const resolvedMarketTrxLabel = isMarketTrxEnabled ? marketTrxLabel : opportunityMarketTrxLabel;
	const resolvedMarketTrxTooltipText = isMarketTrxEnabled ? marketTrxTooltipText : opportunityMarketTrxTooltipText;
	const resolvedMarketTrxValue = isMarketTrxEnabled
		? selectedOpportunityRow?.marketTRx
		: selectedOpportunityRow?.opportunityMarketTRx;
	const oppScoreBadgeText = getConfigValue(appState.config?.Target?.SelectHcp?.OppScore?.Badge?.Text, appState)?.Value || 'FOR INTERNAL REFERENCE ONLY – DO NOT DETAIL.';

	const [activeModalTooltip, setActiveModalTooltip] = useState(null);
	const providerTooltipTarget = useRef(null);
	const oppScoreTooltipTarget = useRef(null);
	const oppTrxTooltipTarget = useRef(null);
	const brandTrxTooltipTarget = useRef(null);
	const marketTrxTooltipTarget = useRef(null);

	useEffect(() => {
		if (!show) {
			setActiveModalTooltip(null);
		}
	}, [show]);

	return (
		<Modal show={show} className='modal-xl' centered onHide={onHide}>
			<Modal.Header className='primary' closeButton>
				<Modal.Title className='type-14 fw-bold text-uppercase opp-score-modal-title'>
					{oppScoreModalTitle}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body className='p-4'>
				<div className='row border-bottom pb-4 mb-0'>
					<div className='col-12 col-md-8'>
						<p className='type-12 fw-bold text-uppercase color-grey-d1 mb-1 opp-score-modal-label'>
							{oppScoreProviderLabel}
							<button
								type='button'
								ref={providerTooltipTarget}
								className='btn-reset'
								onMouseOver={() => setActiveModalTooltip('provider')}
								onMouseOut={() => setActiveModalTooltip(null)}
								onFocus={() => setActiveModalTooltip('provider')}
								onBlur={() => setActiveModalTooltip(null)}
							>
								<span className='fa-regular fa-circle-info ms-1'></span>
							</button>
							<Tooltip
								text={oppScoreProviderTooltipText}
								placement='bottom'
								ref={providerTooltipTarget}
								showTooltip={activeModalTooltip === 'provider' && !!oppScoreProviderTooltipText}
								index='opp-score-provider-tooltip'
							/>
						</p>
						<p className='type-16 fw-bold mb-0'>{selectedOpportunityRow?.displayName || '-'}</p>
					</div>
					<div className='col-12 col-md-4 mt-3 mt-md-0 d-flex flex-column align-items-md-end'>
						<p className='type-12 fw-bold text-uppercase color-grey-d1 mb-1 opp-score-modal-label'>
							{oppScoreMetricLabel}
							<button
								type='button'
								ref={oppScoreTooltipTarget}
								className='btn-reset'
								onMouseOver={() => setActiveModalTooltip('oppScore')}
								onMouseOut={() => setActiveModalTooltip(null)}
								onFocus={() => setActiveModalTooltip('oppScore')}
								onBlur={() => setActiveModalTooltip(null)}
							>
								<span className='fa-regular fa-circle-info ms-1'></span>
							</button>
							<Tooltip
								text={oppScoreMetricTooltipText}
								placement='bottom'
								ref={oppScoreTooltipTarget}
								showTooltip={activeModalTooltip === 'oppScore' && !!oppScoreMetricTooltipText}
								index='opp-score-metric-tooltip'
							/>
						</p>
						<span className={getOpportunityScoreClassName(selectedOpportunityRow?.opportunityScore)}>
							{getOpportunityScoreDisplay(selectedOpportunityRow?.opportunityScore)}
						</span>
					</div>
				</div>
				<div className='row pt-4'>
					<div className='col-12 col-md-4'>
						<p className='type-12 fw-bold text-uppercase color-grey-d1 mb-1 opp-score-modal-label'>
							{oppTrxLabel}
							<button
								type='button'
								ref={oppTrxTooltipTarget}
								className='btn-reset'
								onMouseOver={() => setActiveModalTooltip('oppTrx')}
								onMouseOut={() => setActiveModalTooltip(null)}
								onFocus={() => setActiveModalTooltip('oppTrx')}
								onBlur={() => setActiveModalTooltip(null)}
							>
								<span className='fa-regular fa-circle-info ms-1'></span>
							</button>
							<Tooltip
								text={oppTrxTooltipText}
								placement='bottom'
								ref={oppTrxTooltipTarget}
								showTooltip={activeModalTooltip === 'oppTrx' && !!oppTrxTooltipText}
								index='opp-score-opptrx-tooltip'
							/>
						</p>
						<p className='type-20 fw-bold mb-0'>
							{formatOpportunityMetricValue(selectedOpportunityRow?.opportunityTRx)}
						</p>
					</div>
					<div className='col-12 col-md-4 mt-3 mt-md-0'>
						<p className='type-12 fw-bold text-uppercase color-grey-d1 mb-1 opp-score-modal-label'>
							{brandTrxLabel}
							<button
								type='button'
								ref={brandTrxTooltipTarget}
								className='btn-reset'
								onMouseOver={() => setActiveModalTooltip('brandTrx')}
								onMouseOut={() => setActiveModalTooltip(null)}
								onFocus={() => setActiveModalTooltip('brandTrx')}
								onBlur={() => setActiveModalTooltip(null)}
							>
								<span className='fa-regular fa-circle-info ms-1'></span>
							</button>
							<Tooltip
								text={brandTrxTooltipText}
								placement='bottom'
								ref={brandTrxTooltipTarget}
								showTooltip={activeModalTooltip === 'brandTrx' && !!brandTrxTooltipText}
								index='opp-score-brandtrx-tooltip'
							/>
						</p>
						<p className='type-20 fw-bold mb-0'>
							{formatOpportunityMetricValue(selectedOpportunityRow?.productTRx)}
						</p>
					</div>
					<div className='col-12 col-md-4 mt-3 mt-md-0'>
						<p className='type-12 fw-bold text-uppercase color-grey-d1 mb-1 opp-score-modal-label'>
							{resolvedMarketTrxLabel}
							<button
								type='button'
								ref={marketTrxTooltipTarget}
								className='btn-reset'
								onMouseOver={() => setActiveModalTooltip('marketTrx')}
								onMouseOut={() => setActiveModalTooltip(null)}
								onFocus={() => setActiveModalTooltip('marketTrx')}
								onBlur={() => setActiveModalTooltip(null)}
							>
								<span className='fa-regular fa-circle-info ms-1'></span>
							</button>
							<Tooltip
								text={resolvedMarketTrxTooltipText}
								placement='bottom'
								ref={marketTrxTooltipTarget}
								showTooltip={activeModalTooltip === 'marketTrx' && !!resolvedMarketTrxTooltipText}
								index='opp-score-markettrx-tooltip'
							/>
						</p>
						<p className='type-20 fw-bold mb-0'>
							{formatOpportunityMetricValue(resolvedMarketTrxValue)}
						</p>
					</div>
				</div>
			</Modal.Body>
			<Modal.Footer className='justify-content-center py-2 text-center opp-score-modal-footer'>
				<p className='mb-0 w-100 text-center opp-score-modal-disclaimer'>
					<span className='fa-regular fa-shield-check me-2'></span>
					{oppScoreBadgeText}
				</p>
			</Modal.Footer>
		</Modal>
	);
}

OpportunityScoreModal.propTypes = {
	show: PropTypes.bool.isRequired,
	onHide: PropTypes.func.isRequired,
	selectedOpportunityRow: PropTypes.object,
	appState: PropTypes.object.isRequired,
	getOpportunityScoreClassName: PropTypes.func.isRequired,
	getOpportunityScoreDisplay: PropTypes.func.isRequired,
	formatOpportunityMetricValue: PropTypes.func.isRequired,
};

export default OpportunityScoreModal;